/**
 * The site scope of a strategy, fitted to its product's sites (CU-869fhuhy6
 * criterion 10; CU-869fkr1a9 QA, out-of-scope bug 1).
 *
 * The wizard keeps the site scope as an inclusion over the product's sites
 * (`toServer` always sends `filterGroupSitesExclusion: false`). A strategy
 * saved before that may hold an exclusion ("every site but Y"). Reading its
 * listed sites as an inclusion turned it into "only Y" — the opposite of what
 * was saved. An exclusion is converted to the equivalent inclusion: the
 * product's sites minus the listed ones, never the listed ones. Every path
 * that saves a strategy goes through here: the wizard (`ProductSites.jsx`)
 * and "Align B to A" (`alignSiteScope`).
 *
 * An exclusion of every site of the product converts to an empty list, and an
 * empty list means every site of the product: `emptied` says so, and the
 * caller refuses to save until a site is picked (Curro, 2026-10-22).
 */

/**
 * @param {number[]} selected  ids stored in `filterGroupSites`
 * @param {boolean} exclusion  `filterGroupSitesExclusion`
 * @param {number[]} allowed   the product's sites (`productSites`)
 * @returns {{ sites: number[], gone: number[], converted: number[] | null, emptied: boolean, changed: boolean }}
 *   `sites` the inclusion to store · `gone` listed sites the product does not
 *   have (inclusion only) · `converted` the excluded sites as stored when an
 *   exclusion was converted, else null · `emptied` an exclusion that leaves no
 *   site of the product · `changed` whether the draft must be rewritten
 */
export function fitSiteScope(selected, exclusion, allowed) {
	const listed = Array.isArray(selected) ? selected : []
	const own = Array.isArray(allowed) ? allowed : []
	// "All sites but none" is every site of the product, including ones added later: the empty
	// inclusion means exactly that, so the meaning is unchanged and nothing needs saying.
	if (exclusion && listed.length === 0) return { sites: [], gone: [], converted: null, emptied: false, changed: true }
	if (exclusion) {
		const sites = own.filter(id => !listed.includes(id))
		return { sites, gone: [], converted: listed, emptied: own.length > 0 && sites.length === 0, changed: true }
	}
	const sites = listed.filter(id => own.includes(id))
	const gone = listed.filter(id => !own.includes(id))
	return { sites, gone, converted: null, emptied: false, changed: gone.length > 0 }
}

/** The line that says an exclusion was converted ("all sites but A, B"). */
export function convertedLabel(ids, labelOf = String) {
	return `all sites but ${ids.map(labelOf).join(', ')}`
}

export const SITES_EMPTIED_ERROR = 'Pick at least one site — this strategy excluded every site of its product.'

/**
 * The site fields "Align B to A" saves for B. An inclusion is saved as it
 * is; an exclusion is converted like the wizard does. Refused (`error`) when
 * the product is unknown (`allowed` null) or nothing of it would be left.
 *
 * @param {{ filterGroupSites?: any[], filterGroupSitesExclusion?: boolean, name?: string }} b
 * @param {number[] | null} allowed  B's product sites, null when its product is not in the catalogue
 * @returns {{ patch: object, error: string | null }}
 */
export function alignSiteScope(b, allowed) {
	if (!b?.filterGroupSitesExclusion) return { patch: {}, error: null }
	const name = b?.name ? `"${b.name}"` : 'This strategy'
	if (!Array.isArray(allowed)) {
		return { patch: {}, error: `${name} has its sites saved as "all sites but …" and its product is not available: open it in the wizard and save it there before aligning.` }
	}
	const ids = (Array.isArray(b.filterGroupSites) ? b.filterGroupSites : []).map(o => Number(o?.value ?? o)).filter(Number.isFinite)
	const fit = fitSiteScope(ids, true, allowed)
	if (fit.emptied) {
		return { patch: {}, error: `${name} excluded every site of its product: open it in the wizard and pick at least one site before aligning.` }
	}
	return { patch: { filterGroupSites: fit.sites, filterGroupSitesExclusion: false }, error: null }
}
